import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/common';
import { queryKeys } from '@/lib/query/client';
import type { NotificationAction } from '@/lib/api/notifications';
import { useDismissNotification } from '@/hooks/useNotifications';
import { logger } from '@/lib/utils/logger';

interface NotificationActionButtonProps {
  action: NotificationAction;
  notificationId: string;
  onDismiss?: () => void;
  onClose?: () => void;
}

export function NotificationActionButton({
  action,
  notificationId,
  onDismiss,
  onClose,
}: NotificationActionButtonProps) {
  const queryClient = useQueryClient();
  const dismiss = useDismissNotification();

  const preferenceMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const parts = key.split('.');
      const section = parts[0] ?? key;
      const field = parts[1] ?? key;
      await apiFetch('/api/v1/users/me/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [section]: { [field]: value } }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.userPreferences() });
      onClose?.();
    },
    onError: (error) => {
      logger.error('Failed to update preference:', error);
    },
  });

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    switch (action.type) {
      case 'navigate_to':
        if (action.path) {
          onClose?.();
          window.location.href = action.path;
        }
        break;
      case 'enable_preference':
        if (action.preferenceKey && action.preferenceValue) {
          preferenceMutation.mutate({
            key: action.preferenceKey,
            value: action.preferenceValue,
          });
        }
        break;
      case 'open_external':
        if (action.url) {
          window.open(action.url, '_blank', 'noopener,noreferrer');
          onClose?.();
        }
        break;
      case 'dismiss':
        dismiss.mutate(notificationId, { onSuccess: () => onDismiss?.() });
        break;
    }
  };

  const isPending = preferenceMutation.isPending || dismiss.isPending;

  return (
    <button
      onClick={handleClick}
      disabled={isPending}
      className="px-3 py-1.5 text-xs font-medium rounded-md
                 bg-sky-50 text-sky-700 hover:bg-sky-100
                 dark:bg-sky-900/30 dark:text-sky-300 dark:hover:bg-sky-900/50
                 transition-colors disabled:opacity-50"
    >
      {isPending ? 'Updating...' : action.label}
    </button>
  );
}
